import { useEffect, useState } from 'react';
import { pathsManifest } from '@zg-rentals/shields-js';
import { useSelector } from 'react-redux';
import { isListingRestrictedSelector, propertyIdSelector } from '../../../selectors';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, ButtonGroup, Heading, ModalDialog, TextButton } from '@zillow/constellation';
import { getApplicationsTabPath, URLS } from '../../../constants/urls';
import RestrictedBodyText from '../RestrictedModal/RestrictedBodyText';
import { useListingUser } from '../../../hooks/useListingUser';
import { emitAccessMfListingModalEvent } from '../../../../client/analytics/clickstreamEvents';

function RestrictedModal() {
  const history = useHistory();
  const location = useLocation();
  const propertyId = useSelector(propertyIdSelector);
  const applicationsTabPath = getApplicationsTabPath(propertyId);
  const currentPath = history.createHref(location);
  const isListingRestricted = useSelector(isListingRestrictedSelector);
  const [isOpen, setIsOpen] = useState(false);
  const { user, listing } = useListingUser();

  useEffect(() => {
    /**
     * The Constellation modal always animates open, which looks clunky
     * when it begins already open on the server side. So we will open it
     * only after it mounts on the client side.
     */
    setIsOpen(
      isListingRestricted &&
        /**
         * Ensure that the modal is never open once the user is actively
         * on the applications tab page.
         */
        currentPath !== applicationsTabPath,
    );
    if (isListingRestricted) {
      emitAccessMfListingModalEvent(listing, user);
    }
  }, [isListingRestricted, currentPath, applicationsTabPath]);

  /**
   * When redirected to this modal from an application page, its URL is kept
   * in the window location to allow the user to return to that application
   * page upon login.
   */
  const loginPath = pathsManifest.zillow.signInPage(currentPath, '');

  function cancelModal() {
    setIsOpen(false);

    /**
     * Once the user cancels the modal, they are effectively back on the
     * applications tab page, so change the window location accordingly.
     */
    history.replace(applicationsTabPath);
  }

  return (
    <ModalDialog
      wide
      shouldCloseOnESCKeyPress
      shouldCloseOnOutsideClick
      {...{
        isOpen,
        header: <Heading level={1}>Access unavailable</Heading>,
        body: <RestrictedBodyText />,
        footer: (
          <ButtonGroup aria-label="Restricted modal actions">
            <TextButton
              {...{
                onClick: cancelModal,
              }}
            >
              Cancel
            </TextButton>
            {isListingRestricted ? (
              <Button
                {...{
                  as: 'a',
                  href: URLS.ZRM,
                  buttonType: 'primary',
                }}
              >
                Return to Zillow
              </Button>
            ) : (
              <Button
                {...{
                  as: 'a',
                  href: loginPath,
                  buttonType: 'primary',
                }}
              >
                Switch accounts
              </Button>
            )}
          </ButtonGroup>
        ),
        onClose: cancelModal,
      }}
    />
  );
}

export default RestrictedModal;
